import { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { usePlatformLoginMutation } from '../../api/endpoints/auth'
import { useAppSelector } from '../../app/store'
import { useEnter } from '../../app/enter'
import { vkLaunchParams } from '../../platform/detect'
import { canStartVkLogin, VK_APP_URL } from '../../platform/vkEntry'
import { PlankButton } from '../../ui'
import { AuthFrame } from './AuthFrame'
import './vkEntry.css'

type Phase = 'outside' | 'working' | 'network' | 'signature' | 'unavailable' | 'failed'
const COPY: Record<Phase, [string, string]> = {
  outside: ['Откройте Межу во ВКонтакте', 'Войдите учётной записью ВКонтакте — и отправляйтесь в путь.'],
  working: ['Входим через ВКонтакте…', 'Подтверждаем вход. Край уже ждёт вас.'],
  network: ['Не удалось связаться с игрой', 'Проверьте соединение и попробуйте войти ещё раз.'],
  signature: ['Не удалось подтвердить вход', 'Откройте приложение заново во ВКонтакте, чтобы продолжить.'],
  unavailable: ['Вход через ВКонтакте пока недоступен', 'Попробуйте войти немного позже.'],
  failed: ['Не удалось войти в игру', 'Попробуйте ещё раз. Если ошибка повторится, откройте приложение заново.'],
}

export function VkEntry() {
  const credential = vkLaunchParams()
  const valid = canStartVkLogin(credential)
  const [phase, setPhase] = useState<Phase>(valid ? 'working' : 'outside')
  const signedIn = useAppSelector(s => s.session.signedIn)
  const [signIn] = usePlatformLoginMutation()
  const enter = useEnter()
  const location = useLocation()
  const requested = (location.state as { from?: string } | null)?.from
  const back = requested?.startsWith('/play') ? requested : undefined
  const started = useRef(false)
  const busy = useRef(false)

  const run = useCallback(async () => {
    if (!valid || busy.current) return
    busy.current = true
    setPhase('working')
    try {
      if (!signedIn) await signIn({ platform: 'vk', credential }).unwrap()
      await enter(back)
    } catch (error) {
      const code = error && typeof error === 'object' && 'code' in error ? error.code : ''
      setPhase(code === 'invalid_signature' ? 'signature'
        : code === 'provider_not_configured' ? 'unavailable'
          : code === 'network' ? 'network' : 'failed')
    } finally {
      busy.current = false
    }
  }, [back, credential, enter, signIn, signedIn, valid])

  useEffect(() => {
    if (!started.current && valid) {
      started.current = true
      void run()
    }
  }, [run, valid])

  const reopen = phase === 'outside' || phase === 'signature'
  const failed = phase !== 'outside' && phase !== 'working'
  return (
    <AuthFrame title="Межа" sub="Тридесятое царство · ВКонтакте" className="auth--vk">
      <div role={failed ? 'alert' : 'status'} className="vk-entry__message">
        <p className="auth__lead">{COPY[phase][0]}</p>
        <p className="auth__alt">{COPY[phase][1]}</p>
      </div>
      {phase === 'working' ? <div className="vk-entry__spinner" aria-hidden="true" /> : (
        <div className="auth__actions">
          {reopen ? <a className="plank plank--gold" href={VK_APP_URL} target="_top" rel="noreferrer">
            <span className="plank__in">{phase === 'outside' ? 'Открыть во ВКонтакте' : 'Открыть заново'}</span>
          </a> : <PlankButton gold onClick={() => void run()}>Повторить</PlankButton>}
        </div>
      )}
    </AuthFrame>
  )
}
